/**
 * Session-scoped WebSocket signaling channel (cookie-authenticated, same-origin).
 *
 * A module-level singleton: created once per browser session and kept alive
 * across React StrictMode remounts and component unmounts. Auto-reconnects
 * with backoff and queues messages while the socket is down (queue capped at
 * 200 frames), flushing on reconnect. Stops reconnecting after an auth
 * rejection (close code 4401, i.e. logged out); acquireSignaling() restarts
 * it on the next login.
 */
let channel = null;

function buildChannel() {
  let ws = null;
  let handlers = new Set();
  let queue = [];
  let retries = 0;
  let reconnectTimer = null;
  let authFailed = false;

  const scheduleReconnect = () => {
    if (reconnectTimer || authFailed) return;
    const delay = Math.min(5000, 500 * 2 ** retries++);
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      connect();
    }, delay);
  };

  const connect = () => {
    if (authFailed) return;
    try {
      const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
      ws = new WebSocket(`${proto}//${window.location.host}/api/ws`); // same-origin, httpOnly cookie rides the handshake
    } catch (e) {
      console.warn("[signaling] connect failed", e);
      scheduleReconnect();
      return;
    }
    ws.onopen = () => {
      retries = 0;
      queue.forEach((m) => {
        try { ws.send(m); } catch (e) { console.warn("[signaling] flush failed", e); }
      });
      queue = [];
    };
    ws.onmessage = (ev) => {
      let msg = null;
      try { msg = JSON.parse(ev.data); } catch { return; }
      handlers.forEach((h) => {
        try { h(msg); } catch (e) { console.error("[signaling] handler error", e); }
      });
    };
    ws.onclose = (ev) => {
      if (ev.code === 4401) {
        authFailed = true; // not logged in anymore — stay quiet until re-acquired
        window.dispatchEvent(new Event("ll:auth-expired"));
        return;
      }
      scheduleReconnect();
    };
    ws.onerror = () => { try { ws.close(); } catch (e) { console.warn("[signaling] close failed", e); } };
  };

  connect();

  return {
    send(obj) {
      const raw = JSON.stringify(obj);
      if (ws && ws.readyState === 1) {
        try { ws.send(raw); return; } catch (e) { console.warn("[signaling] send failed", e); }
      }
      queue.push(raw);
      if (queue.length > 200) queue = queue.slice(-200);
      if (!ws || ws.readyState >= 2) scheduleReconnect();
    },
    onMessage(h) {
      handlers.add(h);
      return () => handlers.delete(h);
    },
    reconnect() {
      if (ws && ws.readyState <= 1) return; // already open/connecting
      authFailed = false;
      retries = 0;
      connect();
    },
  };
}

export function acquireSignaling() {
  if (!channel) {
    channel = buildChannel();
  } else {
    channel.reconnect(); // no-op if the socket is healthy
  }
  return channel;
}

export function releaseSignaling() {
  // Intentionally no-op: the channel lives for the whole session so
  // StrictMode double-mounts and page-internal unmounts never kill it.
}
